"use client";

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, UserCircle2, LogOut, CalendarDays, ListOrdered, LayoutDashboard } from "lucide-react";
import { NAV, BRAND } from "@/lib/marketing/copy";
import { CTAButton } from "@/components/common/CTAButton";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";

export function ZaklynNavbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Ensure header height variable is stable to prevent overlap
  useEffect(() => {
    const applyHeaderHeightVar = () => {
      const isMd = window.matchMedia("(min-width: 768px)").matches;
      const heightPx = isMd ? 80 : 64; // matches h-20 (md) and h-16 (mobile)
      document.documentElement.style.setProperty("--site-header-height", `${heightPx}px`);
    };
    applyHeaderHeightVar();
    window.addEventListener("resize", applyHeaderHeightVar);
    return () => window.removeEventListener("resize", applyHeaderHeightVar);
  }, []);

  // Track scroll to update navbar style
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen]);

  // CTA respects auth: if not logged in, go to login with redirect
  const ctaHref = isAuthenticated
    ? "/customer/measurements/book"
    : `/login?redirect=${encodeURIComponent("/customer/measurements/book")}`;

  const closeMobile = () => setIsMobileMenuOpen(false);

  async function handleLogout() {
    await logout();
    closeMobile();
    if (location.pathname.startsWith("/customer")) {
      navigate("/", { replace: true });
    }
  }

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className={cn(
          "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
          isScrolled ? "bg-white/95 backdrop-blur-md shadow-zaklyn" : "bg-transparent"
        )}
      >
        <nav className="zaklyn-container">
          {/* Keep a stable inner height so layout spacing is consistent */}
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Brand */}
            <Link to="/" className="flex items-center">
              <span className="font-display text-2xl md:text-3xl font-semibold text-foreground">
                {BRAND.name}
              </span>
            </Link>

            {/* Desktop: nav links */}
            <div className="hidden md:flex items-center gap-8">
              {!isAuthenticated ? (
                // Public links when logged out
                <>
                  {NAV.links.map((link) => (
                    <Link
                      key={link.href}
                      to={link.href}
                      className="text-sm font-medium text-foreground/80 hover:text-foreground transition-colors"
                    >
                      {link.label}
                    </Link>
                  ))}
                </>
              ) : (
                // Auth-only links when logged in: Dashboard, Orders, Appointments
                <>
                  <Link
                    to="/customer/dashboard"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground/80 hover:text-foreground transition-colors"
                  >
                    <LayoutDashboard className="h-4 w-4" />
                    Dashboard
                  </Link>
                  <Link
                    to="/customer/orders"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground/80 hover:text-foreground transition-colors"
                  >
                    <ListOrdered className="h-4 w-4" />
                    Orders
                  </Link>
                  <Link
                    to="/customer/appointments"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground/80 hover:text-foreground transition-colors"
                  >
                    <CalendarDays className="h-4 w-4" />
                    Appointments
                  </Link>
                </>
              )}
            </div>

            {/* Desktop: right side actions */}
            <div className="hidden md:flex items-center gap-4">
              {!isAuthenticated ? (
                <>
                  <Link
                    to={`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`}
                    className="text-sm font-medium text-foreground/80 hover:text-foreground transition-colors"
                  >
                    {NAV.login}
                  </Link>
                  <CTAButton href={ctaHref} size="sm">
                    {NAV.cta}
                  </CTAButton>
                </>
              ) : (
                <>
                  {/* User chip */}
                  <Link
                    to="/customer/dashboard"
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary hover:bg-secondary/80 text-sm font-medium text-foreground transition-colors"
                    title="View dashboard"
                  >
                    <UserCircle2 className="h-5 w-5" />
                    <span className="truncate max-w-[12rem]">{user?.name || "User"}</span>
                    <span className="text-xs text-muted-foreground">ID: {user?.id}</span>
                  </Link>

                  {/* Logout */}
                  <button
                    onClick={handleLogout}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground/80 hover:text-foreground transition-colors"
                    title="Logout"
                  >
                    <LogOut className="h-4 w-4" />
                    Logout
                  </button>

                  {/* CTA */}
                  <CTAButton href={ctaHref} size="sm">
                    {NAV.cta}
                  </CTAButton>
                </>
              )}
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMobileMenuOpen(true)}
              className="md:hidden p-2 -mr-2 text-foreground"
              aria-label="Open menu"
            >
              <Menu className="h-6 w-6" />
            </button>
          </div>
        </nav>
      </motion.header>

      {/* Mobile overlay + drawer */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-foreground/20 backdrop-blur-sm z-50 md:hidden"
              onClick={closeMobile}
            />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="fixed top-0 right-0 bottom-0 w-80 bg-white z-50 shadow-zaklyn-lg md:hidden"
            >
              <div className="flex flex-col h-full">
                {/* Mobile header */}
                <div className="flex items-center justify-between p-4 border-b border-border">
                  <span className="font-display text-xl font-semibold text-foreground">
                    {BRAND.name}
                  </span>
                  <button
                    onClick={closeMobile}
                    className="p-2 -mr-2 text-foreground"
                    aria-label="Close menu"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                {/* Mobile content */}
                <div className="flex-1 overflow-y-auto py-6 px-4">
                  {!isAuthenticated ? (
                    <>
                      {/* Public links when logged out */}
                      <div className="space-y-1">
                        {NAV.links.map((link, index) => (
                          <motion.div
                            key={link.href}
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.05 }}
                          >
                            <Link
                              to={link.href}
                              onClick={closeMobile}
                              className="block py-3 px-4 text-lg font-medium text-foreground hover:bg-secondary rounded-xl transition-colors"
                            >
                              {link.label}
                            </Link>
                          </motion.div>
                        ))}
                      </div>

                      <div className="mt-6 pt-6 border-t border-border space-y-2">
                        <Link
                          to={`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`}
                          onClick={closeMobile}
                          className="block py-3 px-4 text-lg font-medium text-foreground hover:bg-secondary rounded-xl transition-colors"
                        >
                          {NAV.login}
                        </Link>

                        <Link
                          to={ctaHref}
                          onClick={closeMobile}
                          className="block py-3 px-4 text-lg font-medium text-primary bg-primary/10 rounded-xl transition-colors"
                        >
                          {NAV.cta}
                        </Link>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Auth-only section when logged in */}
                      <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-secondary mb-4">
                        <UserCircle2 className="h-6 w-6 text-foreground" />
                        <div className="min-w-0">
                          <div className="text-sm font-semibold truncate">{user?.name || "User"}</div>
                          <div className="text-xs text-muted-foreground">ID: {user?.id}</div>
                        </div>
                      </div>

                      <Link
                        to="/customer/dashboard"
                        onClick={closeMobile}
                        className="block py-3 px-4 text-lg font-medium text-foreground hover:bg-secondary rounded-xl transition-colors"
                      >
                        Dashboard
                      </Link>

                      <Link
                        to="/customer/orders"
                        onClick={closeMobile}
                        className="block py-3 px-4 text-lg font-medium text-foreground hover:bg-secondary rounded-xl transition-colors"
                      >
                        Orders
                      </Link>

                      <Link
                        to="/customer/appointments"
                        onClick={closeMobile}
                        className="block py-3 px-4 text-lg font-medium text-foreground hover:bg-secondary rounded-xl transition-colors"
                      >
                        Appointments
                      </Link>

                      <Link
                        to="/customer/measurements/book"
                        onClick={closeMobile}
                        className="block py-3 px-4 text-lg font-medium text-foreground hover:bg-secondary rounded-xl transition-colors"
                      >
                        Book Measurement
                      </Link>

                      <button
                        onClick={handleLogout}
                        className="w-full text-left py-3 px-4 text-lg font-medium text-foreground hover:bg-secondary rounded-xl transition-colors mt-2"
                      >
                        Logout
                      </button>
                    </>
                  )}
                </div>

                {/* Mobile footer CTA */}
                <div className="p-4 border-t border-border">
                  <CTAButton href={ctaHref} className="w-full" onClick={closeMobile}>
                    {NAV.cta}
                  </CTAButton>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}